import { PathRouteProps, Route } from 'react-router-dom'
import { LazyRoute } from 'routes/_utils/lazy_route/lazy_route'
import { ComponentType, ReactNode } from 'react'
import { IndexRouteProps } from 'react-router/dist/lib/components'
import { PermissionType } from 'domain/_entities/permission.entity'
import { FeatureSwitch } from 'models/customer'
import { useUserContext } from 'domain/users/user_provider/user.provider'

/**
 * File import path fn to lazy load a Component
 * @category Routes
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ImportFileFn = () => Promise<{ default: ComponentType<any> }>

/**
 * Route Definitions for transformation into RouteObjects or ReactNode elements
 * @category Routes
 */
export type RouteDefs = {
  /** The file import path fn to lazy load a Component */
  file: ImportFileFn
  /** The Route path */
  path?: string
  /** The route params ids */
  params?: string[]
  /** Whether the route is an index route */
  index?: boolean
  /** The children route defs */
  children?: RouteDefs[]
  /** Permission based access to the Routes. */
  permissions?: PermissionType[]
  /** Feature based access to the Routes. */
  features?: FeatureSwitch[]
}

/**
 * Transforms RouteDefs into Route Elements
 * @param routes
 * @param params
 */
export const useRoutesFn = (
  routes: RouteDefs[],
  params: string[] = []
): ReactNode => {
  const { hasUserPermissions, hasUserEnabledFeatures } = useUserContext()
  // check if the route should be included based on user permissions and enabled features
  const shouldIncludeRoutesFn = (route: RouteDefs) =>
    (!route.features || hasUserEnabledFeatures(route.features)) &&
    hasUserPermissions(route.permissions ?? [])
  const restrictedRoutes = (routes: RouteDefs[]): RouteDefs[] =>
    [...routes]
      .filter(
        route =>
          (!route.children && shouldIncludeRoutesFn(route)) ||
          // if any of the children has permissions and enabled features, show the parent
          route.children?.some(child => shouldIncludeRoutesFn(child))
      )
      .map(route => {
        const childrenObj = route.children
          ? { children: restrictedRoutes(route.children) }
          : {}
        if (childrenObj.children?.length && !childrenObj.children[0].index) {
          const tmpChild = childrenObj.children[0]
          childrenObj.children.splice(1, 0, { ...tmpChild })
          delete tmpChild.path
          childrenObj.children[0] = {
            ...tmpChild,
            index: true,
          }
        }
        return {
          ...route,
          ...childrenObj,
        }
      })
  const routesMapPredicate = (route: RouteDefs, idx: number) => {
    const pathRouterProps: PathRouteProps = {
      path: route.path,
      index: false,
      element: <LazyRoute {...route} params={[...params]} />,
    }

    const key = `${route.path}:${idx}`

    if (!route.index) {
      return (
        <Route {...pathRouterProps} key={key}>
          {route.children?.map(routesMapPredicate)}
        </Route>
      )
    }

    const indexRouterProps = {
      ...pathRouterProps,
      index: true,
    } as IndexRouteProps

    return <Route {...indexRouterProps} key={key} />
  }
  return restrictedRoutes(routes).map(routesMapPredicate)
}
